import {
  oidcClientId,
  oidcExpireNotification,
  oidcPostLogoutRedirectUrl,
  oidcRedirectUrl,
  oidcSilentRedirectUrl,
  oidcUrl,
} from '@/configuration'
import { fromUnixTime } from 'date-fns'
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts'

const log = globalThis.console

export const oidcManager = new UserManager({
  userStore: new WebStorageStateStore({}),
  authority: oidcUrl,
  client_id: oidcClientId,
  redirect_uri: oidcRedirectUrl,
  silent_redirect_uri: oidcSilentRedirectUrl,
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: oidcExpireNotification,
  response_type: 'code',
  scope: 'openid profile email username aura_shows',
  post_logout_redirect_uri: oidcPostLogoutRedirectUrl,
  loadUserInfo: true,
})

export class OIDCError extends Error {}
export class OIDCNoUserError extends OIDCError {}

export async function getUser(): Promise<User> {
  let user: User | null

  try {
    user = await oidcManager.getUser()
  } catch (e) {
    log.error('Could not fetch OIDC user object.', e)
    throw new OIDCError('Unable to fetch OIDC user.')
  }

  if (user === null) {
    throw new OIDCNoUserError()
  }

  if (user.expires_at) {
    const now = new Date()
    const expiry = fromUnixTime(user.expires_at)
    // If we have a token, but it has already expired, log out the user
    if (expiry <= now) {
      log.debug(`Token has already expired at ${expiry.toLocaleString()}. Logging out...`)
      await logoutRedirect()
      throw new OIDCNoUserError()
    }
  }

  return user
}

export async function logoutRedirect() {
  try {
    await oidcManager.signoutRedirect()
    // clear steering session cookies if possible
    document.cookie = ''
    log.debug('Signed out.')
  } catch (e) {
    log.error('Unable to logout.', e)
  }
}

export async function loginRedirect(nextURL?: string) {
  try {
    await oidcManager.signinRedirect({
      state: { next: nextURL ?? null },
    })
  } catch (e) {
    log.error('Unable to login', e)
    throw e
  }
}
